import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, Check, MessageSquare, Link2, Loader2, Send } from "lucide-react";
import { api, API, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeading from "@/components/PageHeading";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEKDAYS = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];

function dayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Calendario() {
  const { user } = useAuth();
  const isClient = user?.role === "cliente";
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState({ items: [], tasks: [] });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickMode, setPickMode] = useState(false);
  const [picked, setPicked] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  const togglePick = (item) => setPicked((p) => (p.some((x) => x.id === item.id) ? p.filter((x) => x.id !== item.id) : [...p, item]));
  const sel = pickMode ? { active: true, ids: picked.map((p) => p.id), toggle: togglePick } : null;
  const exitPick = () => { setPickMode(false); setPicked([]); setBulkOpen(false); };

  useEffect(() => {
    api.get("/brands").then(({ data }) => {
      setBrands(data);
      if (data.length) setBrandId(data[0].id);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const range = useMemo(() => {
    if (view === "week") {
      const start = new Date(cursor);
      start.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { start, end };
    }
    return {
      start: new Date(cursor.getFullYear(), cursor.getMonth(), 1),
      end: new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0),
    };
  }, [cursor, view]);

  const load = useCallback(() => {
    if (!brandId) return;
    setLoading(true);
    api.get("/calendar", { params: { brand_id: brandId, start: dayKey(range.start), end: dayKey(range.end) } })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [brandId, range]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const map = {};
    data.items.forEach((i) => { if (i.publish_date) (map[i.publish_date] = map[i.publish_date] || { items: [], tasks: [] }).items.push(i); });
    data.tasks.forEach((t) => { if (t.due_date) (map[t.due_date] = map[t.due_date] || { items: [], tasks: [] }).tasks.push(t); });
    return map;
  }, [data]);

  const move = (dir) => {
    const d = new Date(cursor);
    if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCursor(d);
  };

  const brand = brands.find((b) => b.id === brandId);

  const copyIcs = () => {
    if (!brand?.ics_token) return;
    navigator.clipboard.writeText(`${API}/ics/brand/${brand.ics_token}.ics`);
    toast.success("Link ICS del brand copiato. Aggiungilo come calendario in abbonamento.");
  };

  const title = view === "week"
    ? `${range.start.getDate()} ${MONTHS[range.start.getMonth()].slice(0, 3)} – ${range.end.getDate()} ${MONTHS[range.end.getMonth()].slice(0, 3)}`
    : `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;

  return (
    <div data-testid="calendario-page">
      <PageHeading kicker="03 · PIANIFICAZIONE" title="Calendario"
        subtitle={isClient ? "I contenuti pianificati per il tuo brand. Approva o lascia una nota." : "Un calendario separato per ciascun brand, condivisibile singolarmente."} />

      <div className="flex flex-wrap items-center gap-3 mb-8">
        {!isClient && (
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="calendar-brand-select" className="field-line max-w-[220px] bg-transparent">
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <div className="flex items-center gap-px bg-black/10 border border-black/20">
          {["month", "week"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`calendar-view-${v}`}
              className={`px-5 py-2 text-[0.65rem] uppercase tracking-widest transition-colors ${view === v ? "bg-black text-greige" : "bg-greige/60 text-stone2e hover:text-ink"}`}>
              {v === "month" ? "Mese" : "Settimana"}
            </button>
          ))}
        </div>
        {!isClient && brand && (
          <button onClick={copyIcs} data-testid="copy-ics-button" className="inline-flex items-center gap-2 border border-black px-4 py-2 text-[0.65rem] uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">
            <Link2 size={13} strokeWidth={1.5} /> Condividi (ICS)
          </button>
        )}
        {!isClient && (
          <button onClick={() => (pickMode ? exitPick() : setPickMode(true))} data-testid="bulk-pick-toggle"
            className={`inline-flex items-center gap-2 border border-black px-4 py-2 text-[0.65rem] uppercase tracking-widest transition-colors ${pickMode ? "bg-black text-greige" : "hover:bg-black hover:text-greige"}`}>
            <Send size={13} strokeWidth={1.5} /> {pickMode ? "Esci da selezione" : "Programmazione rapida"}
          </button>
        )}
      </div>

      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-2xl md:text-3xl text-ink" data-testid="calendar-title">{title}</h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(new Date())} data-testid="calendar-today" className="border border-black px-4 py-1.5 text-[0.65rem] uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">Oggi</button>
          <button onClick={() => move(-1)} data-testid="calendar-prev" className="border border-black p-1.5 hover:bg-black hover:text-greige transition-colors" aria-label="Precedente"><ChevronLeft size={16} strokeWidth={1.5} /></button>
          <button onClick={() => move(1)} data-testid="calendar-next" className="border border-black p-1.5 hover:bg-black hover:text-greige transition-colors" aria-label="Successivo"><ChevronRight size={16} strokeWidth={1.5} /></button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-stone2e" size={24} /></div>
      ) : brands.length === 0 ? (
        <p className="font-editorial text-xl text-stone2e italic py-12 text-center">Nessun brand disponibile.</p>
      ) : view === "month" ? (
        <MonthGrid cursor={cursor} byDay={byDay} onSelect={setSelected} sel={sel} />
      ) : (
        <WeekGrid range={range} byDay={byDay} onSelect={setSelected} sel={sel} />
      )}

      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4">
        <span className="flex items-center gap-2 font-mono2e text-[0.62rem] text-stone2e"><span className="inline-block w-3 h-3 bg-black" /> CONTENUTO</span>
        <span className="flex items-center gap-2 font-mono2e text-[0.62rem] text-stone2e"><span className="inline-block w-3 h-3 bg-white border border-black/30" /> TASK</span>
      </div>

      {selected && <ItemModal item={selected} isClient={isClient} onClose={() => setSelected(null)} onChanged={load} />}

      {pickMode && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-black text-greige px-5 py-3.5 flex flex-wrap items-center justify-between gap-3" data-testid="bulk-bar">
          <p className="font-mono2e text-[0.68rem] tracking-widest" data-testid="bulk-count">
            {picked.length} CONTENUT{picked.length === 1 ? "O" : "I"} SELEZIONAT{picked.length === 1 ? "O" : "I"}
          </p>
          <div className="flex items-center gap-3">
            <button onClick={exitPick} data-testid="bulk-cancel" className="meta-label text-greige/70 hover:text-greige">Annulla</button>
            <button onClick={() => setBulkOpen(true)} disabled={!picked.length} data-testid="bulk-open-panel"
              className="inline-flex items-center gap-2 bg-greige text-ink px-5 py-2 text-[0.65rem] uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-50">
              <Send size={13} strokeWidth={1.75} /> Invia a PostPickr
            </button>
          </div>
        </div>
      )}

      {bulkOpen && (
        <BulkScheduleDialog items={picked} onClose={() => setBulkOpen(false)}
          onDone={() => { exitPick(); load(); }} />
      )}
    </div>
  );
}

function BulkScheduleDialog({ items, onClose, onDone }) {
  const [channels, setChannels] = useState(null);
  const [defaultCh, setDefaultCh] = useState([]);
  const [perItem, setPerItem] = useState({});
  const [action, setAction] = useState("draft");
  const [mode, setMode] = useState("same");
  const [when, setWhen] = useState("");
  const [timeOfDay, setTimeOfDay] = useState("09:00");
  const [target, setTarget] = useState("feed");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/postpickr/post-channels")
      .then(({ data }) => setChannels(data.channels || []))
      .catch((err) => { setChannels([]); toast.error(formatApiError(err.response?.data?.detail)); });
  }, []);

  const chFor = (id) => (perItem[id] !== undefined ? perItem[id] : defaultCh);
  const toggleDefault = (cid) => setDefaultCh((s) => (s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]));
  const toggleItemCh = (itemId, cid) => setPerItem((p) => {
    const cur = p[itemId] !== undefined ? p[itemId] : defaultCh;
    const next = cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid];
    return { ...p, [itemId]: next };
  });

  const send = async () => {
    const payload = items.map((i) => ({ item_id: i.id, channel_ids: chFor(i.id) })).filter((x) => x.channel_ids.length);
    if (!payload.length) { toast.error("Seleziona almeno un canale"); return; }
    if (payload.length < items.length) {
      toast.info(`${items.length - payload.length} contenuti senza canali verranno saltati`);
    }
    if (action === "schedule" && mode === "same" && !when) { toast.error("Indica data e ora"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ped/postpickr-bulk-publish", {
        items: payload, action, target, mode,
        schedule_time: action === "schedule" && mode === "same" ? when : null,
        time_of_day: timeOfDay,
      });
      setResults(data.results);
      if (data.sent) toast.success(`${data.sent} contenuti inviati a PostPickr`);
      if (data.failed) toast.error(`${data.failed} contenuti non inviati`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={onClose} data-testid="bulk-dialog">
      <div className="bg-greige border border-black/20 max-w-2xl w-full p-7 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="meta-label mb-2">PROGRAMMAZIONE RAPIDA · {items.length} CONTENUTI</p>
            <h4 className="font-display text-2xl text-ink">Invia a PostPickr</h4>
          </div>
          <button onClick={onClose} data-testid="bulk-dialog-close" className="text-stone2e hover:text-ink"><X size={20} strokeWidth={1.5} /></button>
        </div>

        {channels === null ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-stone2e" size={22} /></div>
        ) : channels.length === 0 ? (
          <p className="font-editorial text-lg text-stone2e italic">Nessun canale PostPickr disponibile.</p>
        ) : (
          <div className="space-y-6">
            <div>
              <p className="meta-label mb-2">CANALI PREDEFINITI (APPLICATI A TUTTI)</p>
              <div className="flex flex-wrap gap-2">
                {channels.map((c) => (
                  <button key={c.id} onClick={() => toggleDefault(c.id)} data-testid={`bulk-default-ch-${c.id}`}
                    className={`px-3 py-1.5 text-[0.65rem] uppercase tracking-wider border transition-colors ${defaultCh.includes(c.id) ? "bg-black text-greige border-black" : "border-black/30 text-stone2e hover:border-black"}`}>
                    {c.platform} · {c.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="meta-label mb-1.5">AZIONE</p>
                <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="bulk-action-select" className="field-line bg-transparent">
                  <option value="draft">Salva come bozza</option>
                  <option value="schedule">Programma</option>
                  <option value="now">Pubblica ora</option>
                </select>
              </div>
              <div>
                <p className="meta-label mb-1.5">POSIZIONE</p>
                <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="bulk-target-select" className="field-line bg-transparent">
                  <option value="feed">Feed</option>
                  <option value="story">Storia</option>
                  <option value="reels">Reels</option>
                </select>
              </div>
            </div>

            {action === "schedule" && (
              <div className="border border-black/20 bg-white/60 p-4 space-y-4" data-testid="bulk-schedule-panel">
                <div className="flex items-center gap-px bg-black/10 border border-black/20 w-fit">
                  {[["same", "Stessa data/ora"], ["per_item", "Data di ogni contenuto"]].map(([v, label]) => (
                    <button key={v} onClick={() => setMode(v)} data-testid={`bulk-mode-${v}`}
                      className={`px-4 py-2 text-[0.62rem] uppercase tracking-widest transition-colors ${mode === v ? "bg-black text-greige" : "bg-greige/60 text-stone2e hover:text-ink"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                {mode === "same" ? (
                  <div>
                    <p className="meta-label mb-1.5">DATA E ORA</p>
                    <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="bulk-when-input" className="field-line" />
                  </div>
                ) : (
                  <div>
                    <p className="meta-label mb-1.5">ORA DI PUBBLICAZIONE (PER OGNI CONTENUTO)</p>
                    <input type="time" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} data-testid="bulk-timeofday-input" className="field-line max-w-[160px]" />
                  </div>
                )}
              </div>
            )}

            <div>
              <p className="meta-label mb-3">CANALI PER CONTENUTO</p>
              <div className="space-y-3">
                {items.map((i) => {
                  const r = results?.find((x) => x.item_id === i.id);
                  return (
                    <div key={i.id} className="border border-black/15 bg-white/50 p-3" data-testid={`bulk-item-${i.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-body text-sm text-ink">{i.title}</p>
                        <span className="meta-label shrink-0">{formatDate(i.publish_date).toUpperCase()}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {channels.map((c) => (
                          <button key={c.id} onClick={() => toggleItemCh(i.id, c.id)} data-testid={`bulk-ch-${i.id}-${c.id}`}
                            className={`px-2 py-1 text-[0.6rem] uppercase tracking-wider border transition-colors ${chFor(i.id).includes(c.id) ? "bg-black text-greige border-black" : "border-black/25 text-stone2e hover:border-black"}`}>
                            {c.name}
                          </button>
                        ))}
                      </div>
                      {r && (
                        <p className={`mt-2 text-[0.66rem] ${r.ok ? "text-emerald-700" : "text-red-600"}`} data-testid={`bulk-result-${i.id}`}>
                          {r.ok ? `Inviato · ${r.channels} canali${r.schedule_time ? ` · ${r.schedule_time.replace("T", " ")}` : ""}` : r.error}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <button onClick={send} disabled={busy} data-testid="bulk-send-button"
              className="inline-flex items-center gap-2 bg-black text-greige px-6 py-3 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} strokeWidth={1.75} />}
              {action === "draft" ? "Crea bozze" : action === "now" ? "Pubblica ora" : "Programma"}
            </button>
            {results && (
              <button onClick={onDone} data-testid="bulk-done-button"
                className="ml-3 border border-black px-6 py-3 text-xs uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">
                Chiudi
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function chipItem(i, onSelect, sel) {
  if (sel?.active) {
    const picked = sel.ids.includes(i.id);
    return (
      <button key={i.id} onClick={() => sel.toggle(i)} data-testid={`cal-pick-${i.id}`} title={i.title}
        className={`flex w-full items-center gap-1.5 px-1.5 py-0.5 text-[0.65rem] leading-tight text-left transition-colors ${picked ? "bg-emerald-700 text-white" : "bg-black/80 text-greige hover:bg-stone2e"}`}>
        <span className={`w-2.5 h-2.5 shrink-0 border ${picked ? "bg-white border-white" : "border-greige/60"}`} />
        <span className="truncate">{i.title}</span>
      </button>
    );
  }
  return (
    <button key={i.id} onClick={() => onSelect(i)} data-testid={`cal-item-${i.id}`} title={i.title}
      className="block w-full text-left px-1.5 py-0.5 text-[0.65rem] leading-tight truncate bg-black text-greige hover:bg-stone2e transition-colors">
      {i.approved ? "✓ " : ""}{i.title}
    </button>
  );
}

function chipTask(t) {
  return (
    <div key={t.id} data-testid={`cal-task-${t.id}`} title={`${t.title} · ${t.item_title}`}
      className="flex items-center gap-1 px-1.5 py-0.5 text-[0.62rem] leading-tight truncate bg-white/80 border border-black/20 text-ink">
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: t.phase_color }} />
      <span className="truncate">{t.title}</span>
    </div>
  );
}

function MonthGrid({ cursor, byDay, onSelect, sel }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  const todayK = dayKey(new Date());

  return (
    <div className="grid grid-cols-7 gap-px bg-black/10 border border-black/10">
      {WEEKDAYS.map((w) => <div key={w} className="bg-greige/80 px-2 py-2 text-center font-mono2e text-[0.6rem] tracking-widest text-stone2e">{w}</div>)}
      {cells.map((d, i) => {
        if (!d) return <div key={`e-${i}`} className="bg-greige/40 min-h-[92px]" />;
        const k = dayKey(d);
        const day = byDay[k] || { items: [], tasks: [] };
        const isToday = k === todayK;
        return (
          <div key={k} data-testid={`cal-day-${k}`} className={`min-h-[92px] p-1.5 ${isToday ? "bg-white" : "bg-greige/60"}`}>
            <span className={`inline-flex items-center justify-center w-6 h-6 font-mono2e text-xs ${isToday ? "bg-black text-greige" : "text-stone2e"}`}>{d.getDate()}</span>
            <div className="mt-1 space-y-1">
              {day.items.slice(0, 2).map((it) => chipItem(it, onSelect, sel))}
              {day.tasks.slice(0, 2).map((t) => chipTask(t))}
              {day.items.length + day.tasks.length > 4 && <p className="font-mono2e text-[0.58rem] text-stone2e px-1.5">+{day.items.length + day.tasks.length - 4}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WeekGrid({ range, byDay, onSelect, sel }) {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(range.start);
    d.setDate(range.start.getDate() + i);
    days.push(d);
  }
  const todayK = dayKey(new Date());
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-px bg-black/10 border border-black/10">
      {days.map((d, i) => {
        const k = dayKey(d);
        const day = byDay[k] || { items: [], tasks: [] };
        const isToday = k === todayK;
        return (
          <div key={k} data-testid={`week-day-${k}`} className={`min-h-[120px] md:min-h-[240px] p-2.5 ${isToday ? "bg-white" : "bg-greige/60"}`}>
            <p className="font-mono2e text-[0.6rem] text-stone2e">{WEEKDAYS[i]}</p>
            <span className={`inline-flex items-center justify-center w-7 h-7 font-mono2e text-sm mt-1 ${isToday ? "bg-black text-greige" : "text-ink"}`}>{d.getDate()}</span>
            <div className="mt-2 space-y-1.5">
              {day.items.map((it) => chipItem(it, onSelect, sel))}
              {day.tasks.map((t) => chipTask(t))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ItemModal({ item, isClient, onClose, onChanged }) {
  const [feedback, setFeedback] = useState([]);
  const [noteMode, setNoteMode] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const loadFeedback = useCallback(() => {
    api.get(`/ped/items/${item.id}/feedback`).then(({ data }) => setFeedback(data)).catch(() => {});
  }, [item.id]);
  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/ped/items/${item.id}/approve`);
      toast.success("Contenuto approvato");
      loadFeedback();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/ped/items/${item.id}/note`, { text: note });
      toast.success("Nota inviata");
      setNote("");
      setNoteMode(false);
      loadFeedback();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="item-modal">
      <div className="bg-greige border border-black/20 max-w-lg w-full p-7 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="meta-label mb-2">{(item.format || "CONTENUTO").toUpperCase()} · {formatDate(item.publish_date).toUpperCase()}</p>
            <h4 className="font-display text-2xl text-ink">{item.title}</h4>
          </div>
          <button onClick={onClose} data-testid="item-modal-close" className="text-stone2e hover:text-ink shrink-0"><X size={20} strokeWidth={1.5} /></button>
        </div>
        {item.text && <p className="font-body text-sm text-stone2e mt-4 whitespace-pre-wrap">{item.text}</p>}

        <div className="flex flex-wrap gap-3 mt-6">
          <button onClick={approve} disabled={busy} data-testid="approve-button"
            className="inline-flex items-center gap-2 bg-black text-greige px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors disabled:opacity-60">
            <Check size={14} strokeWidth={1.75} /> Approva
          </button>
          <button onClick={() => setNoteMode(!noteMode)} data-testid="note-toggle-button"
            className="inline-flex items-center gap-2 border border-black px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">
            <MessageSquare size={14} strokeWidth={1.5} /> Commenta con nota
          </button>
        </div>

        {noteMode && (
          <div className="mt-5">
            <textarea rows={3} autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="Scrivi la tua nota…" data-testid="note-textarea" className="field-line resize-none" />
            <button onClick={sendNote} disabled={busy} data-testid="send-note-button" className="mt-3 bg-black text-greige px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-60">Invia nota</button>
          </div>
        )}

        {feedback.length > 0 && (
          <div className="mt-7">
            <p className="meta-label mb-3">CRONOLOGIA FEEDBACK</p>
            <div className="space-y-2.5">
              {feedback.map((f) => (
                <div key={f.id} className="flex items-start gap-2.5">
                  {f.type === "approvazione"
                    ? <Check size={14} strokeWidth={2} className="text-emerald-700 mt-0.5 shrink-0" />
                    : <MessageSquare size={14} strokeWidth={1.5} className="text-stone2e mt-0.5 shrink-0" />}
                  <p className="font-body text-xs text-ink">
                    <span className="font-medium">{f.user_name}</span> {f.type === "approvazione" ? "ha approvato" : `— «${f.text}»`}
                    <span className="meta-label ml-2">{formatDate(f.created_at).toUpperCase()}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
